import * as React from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { User } from 'constants/types';
import { apiRequest } from 'api-client';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';

export function ResetPasswordDialog({ user, onClose }: { user: User | null; onClose?: () => void }) {
  const [open, setOpen] = React.useState(false);
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [passwordError, setPasswordError] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [passwordStrength, setPasswordStrength] = React.useState({ valid: false, message: "" });

  React.useEffect(() => {
    if (user) setOpen(true);
  }, [user]);

  const handleClose = () => {
    setOpen(false);
    onClose?.();
    setPassword('');
    setConfirmPassword('');
    setPasswordError(false);
    setPasswordStrength({ valid: false, message: "" });
  };

  const validatePassword = (pwd: string) => {
    if (pwd.length < 8) return { valid: false, message: "Must be at least 8 characters long." };

    const hasUpper = /[A-Z]/.test(pwd);
    const hasLower = /[a-z]/.test(pwd);
    const hasDigit = /[0-9]/.test(pwd);

    const passed = [hasUpper, hasLower, hasDigit].filter(Boolean).length;

    if (passed < 3)
      return { valid: false, message: "Use at least 3 of: uppercase, lowercase, number." };

    return { valid: true, message: "Strong password." };
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    const result = validatePassword(value);
    setPasswordStrength(result);
    setPasswordError(confirmPassword !== '' && value !== confirmPassword);
  };

  const handleConfirmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setConfirmPassword(value);
    setPasswordError(password !== value && value !== '');
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!passwordStrength.valid || password !== confirmPassword) return;

    setLoading(true);
    console.log("Reset password for:", user?.email, "to", password);
    apiRequest.put(`/users/${user?.id}/password`, { newPassword: password }).then(() => {
      toast.success(`Password for user ${user?.email} has been reset successfully.`);
      handleClose();
    }).catch((error) => {
      console.error("Error resetting password:", error);
      toast.error(`Failed to reset password: ${error.message}`);
    }).finally(() => {
      setLoading(false);
    });
  };

  if (!user) return null;

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Reset Password for {user.email}</DialogTitle>
      <DialogContent>
      	<DialogContentText>
          Please enter a new password that meets the following security requirements:
        </DialogContentText>

        <Box component="ul" sx={{ mt: 1, mb: 2, pl: 3 }}>
          <li>At least 8 characters long</li>
          <li>Includes at least 3 of the following: uppercase, lowercase, number</li>
        </Box>
        <form id="reset-password-form" onSubmit={handleSubmit}>
          <TextField
            autoFocus
            required
            margin="dense"
            name="password"
            label="New Password"
            type={showPassword ? "text" : "password"}
            fullWidth
            variant="standard"
            autoComplete="new-password"
            value={password}
            onChange={handlePasswordChange}
            error={!passwordStrength.valid && password.length > 0}
            helperText={passwordStrength.message}
          />
          <TextField
            required
            margin="dense"
            name="confirmPassword"
            label="Confirm New Password"
            type={showPassword ? "text" : "password"}
            fullWidth
            variant="standard"
            autoComplete="confirm-new-password"
            value={confirmPassword}
            onChange={handleConfirmChange}
            error={passwordError}
            helperText={passwordError ? "Passwords do not match" : ""}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showPassword}
                onChange={(e) => setShowPassword(e.target.checked)}
              />
            }
            label="Show password"
          />
        </form>

        {password && (
          <Box mt={2}>
            <Typography
              variant="body2"
              color={passwordStrength.valid ? "success.main" : "error.main"}
            >
              {passwordStrength.message}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <LoadingButton
          type="submit"
          form="reset-password-form"
          disabled={!passwordStrength.valid || password !== confirmPassword}
          loading={loading}
        >
          Reset
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
